import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Action, State, StateContext } from '@ngxs/store';
import { GroupDetailsDto } from '@smatchy/sdk';
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  GetMyGroupInvitationsAction,
  AcceptGroupInvitationAction,
  DeclineGroupInvitationAction,
  RemoveGroupFromInvitationsAction,
} from './my-group-invitations.actions';
import { environment } from '../../../../../environments/environment';
import { AnalyticsService } from '../../../services/analytics.service';

export interface MyGroupInvitationsStateModel {
  invitations: GroupDetailsDto[];
  loading: boolean;
  processingGroupId: string | null;
}

const defaults: MyGroupInvitationsStateModel = {
  invitations: [],
  loading: false,
  processingGroupId: null,
};

@State<MyGroupInvitationsStateModel>({
  name: 'myGroupInvitations',
  defaults,
})
@Injectable()
export class MyGroupInvitationsState {
  constructor(
    protected http: HttpClient,
    private readonly _analytics: AnalyticsService,
  ) {}

  @Action(GetMyGroupInvitationsAction)
  getMyGroupInvitations({ patchState }: StateContext<MyGroupInvitationsStateModel>) {
    patchState({ loading: true });

    return this.http
      .get<{ value: { groups: GroupDetailsDto[] } }>(
        `${environment.api}/api/v1/groups/my-invitations`
      )
      .pipe(
        tap((res) => {
          patchState({
            loading: false,
            invitations: res.value?.groups || [],
          });
        }),
        catchError(() => {
          patchState({ loading: false });
          return of(null);
        })
      );
  }

  @Action(AcceptGroupInvitationAction)
  acceptGroupInvitation(
    { patchState, dispatch }: StateContext<MyGroupInvitationsStateModel>,
    { groupId }: AcceptGroupInvitationAction
  ) {
    patchState({ processingGroupId: groupId });

    return this.http
      .post(`${environment.api}/api/v1/groups/${groupId}/accept-my-invitation`, {})
      .pipe(
        tap(() => {
          // The one moment the app can observe real membership. The request
          // path emits group_join_request instead, since it only leaves the
          // user pending.
          this._analytics.logGroupJoin({ groupId });
          dispatch(new RemoveGroupFromInvitationsAction(groupId));
          patchState({ processingGroupId: null });
        }),
        catchError(() => {
          patchState({ processingGroupId: null });
          return of(null);
        })
      );
  }

  @Action(DeclineGroupInvitationAction)
  declineGroupInvitation(
    { patchState, dispatch }: StateContext<MyGroupInvitationsStateModel>,
    { groupId }: DeclineGroupInvitationAction
  ) {
    patchState({ processingGroupId: groupId });

    return this.http
      .post(`${environment.api}/api/v1/groups/${groupId}/decline-my-invitation`, {})
      .pipe(
        tap(() => {
          dispatch(new RemoveGroupFromInvitationsAction(groupId));
          patchState({ processingGroupId: null });
        }),
        catchError(() => {
          patchState({ processingGroupId: null });
          return of(null);
        })
      );
  }

  @Action(RemoveGroupFromInvitationsAction)
  removeGroupFromInvitations(
    { getState, patchState }: StateContext<MyGroupInvitationsStateModel>,
    { groupId }: RemoveGroupFromInvitationsAction
  ) {
    const state = getState();
    patchState({
      invitations: state.invitations.filter((g) => g._id !== groupId),
    });
  }
}

